import { TweenMax, TimelineMax } from 'gsap';
import { css } from '@emotion/css';
import { ReactText, useEffect, useRef } from 'react';
import { ScrollMagicPluginGsap } from 'scrollmagic-plugin-gsap';

interface WordsSplitProps {
    children: ReactText;
    lines: number;
    scrollRatio?: number;
    linesSpacing?: number;
    lineHeight?: number;
}
/**
 * 文字视觉差滚动动画组件
 * @props children - -文本
 * @props lines - -文本行数
 * @props scrollRatio - -滚动长度倍数(默认1倍,文本高度*n)
 * @props linesSpacing - -每行文本滚动视觉差间距(默认10px)
 * @props lineHeight - -文本行高(默认1.3)
 * @returns
 */
export default function ScrollWordsLayering({ children: text, lines, scrollRatio = 1, linesSpacing = 10, lineHeight = 1.3 }: WordsSplitProps) {
    const scrollBoxElem = useRef<HTMLDivElement>(null);
    const nodes = [...new Array(lines)];

    useEffect(() => {
        let controller: any;
        let sceneIn: any;
        let sceneOut: any;
        const timeLines: any[] = [];

        if (typeof window !== 'undefined') {
            (async () => {
                const ScrollMagic = (await import('scrollmagic')).default;
                ScrollMagicPluginGsap(ScrollMagic, TweenMax, TimelineMax);
                const { clientHeight = 50, childNodes = [] } = scrollBoxElem.current || {};

                controller = new ScrollMagic.Controller();

                const scrollHeight = clientHeight * scrollRatio;

                sceneIn = createAn({
                    offset: 0,
                    triggerHook: 'onEnter',
                    duration: scrollHeight,
                    from: (index: number) => ({ translateY: linesSpacing * (index + 1), ease: 'ease-out' }),
                    to: () => ({
                        translateY: 0,
                        ease: 'ease-out',
                    }),
                });

                sceneOut = createAn({
                    offset: -scrollHeight / 2,
                    triggerHook: 'onLeave',
                    duration: scrollHeight,
                    from: () => ({
                        translateY: 0,
                        ease: 'ease-in',
                    }),
                    to: (index: number) => ({ translateY: -linesSpacing * (childNodes.length - index), ease: 'ease-in' }),
                });

                function createAn({ triggerHook, offset, duration, from, to }: any) {
                    const scene = new ScrollMagic.Scene({
                        triggerElement: scrollBoxElem.current, //触发元素
                        offset,
                        triggerHook,
                        duration,
                    });

                    const animation = new TimelineMax();
                    childNodes.forEach((i, index) => {
                        const tl = new TimelineMax();
                        const ft = TweenMax.fromTo(i, 3, from(index), to(index));
                        tl.add(ft);
                        animation.add(tl, 0);
                        timeLines.push(tl);
                    });
                    timeLines.push(animation);
                    scene.setTween(animation);
                    return scene;
                }

                controller.addScene(sceneIn).addScene(sceneOut);
            })();
        }

        return () => {
            timeLines.forEach((i) => i && i.kill());
            sceneIn && sceneIn.destroy();
            controller && controller.destroy();
        };
    }, []);

    return (
        <div className="ScrollWordsLayering" ref={scrollBoxElem}>
            {nodes.map((i, idx) => {
                return (
                    <div
                        key={idx}
                        className={css`
                            overflow: hidden;
                            line-height: ${lineHeight}em;
                            height: ${lineHeight}em;
                        `}
                    >
                        <span
                            className={css`
                                display: block;
                                margin-top: -${lineHeight * idx}em;
                            `}
                        >
                            {text}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
